import {
  ClockCircleOutlined,
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  UploadOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Popconfirm, Tag, Tooltip, message } from "antd";
import type { TableColumnsType } from "antd";
import { customerApi } from "api/customer.api";
import { partnerApi } from "api/partner.api";
import { staffApi } from "api/staff.api";
import { AppButton, AppSelect, AppTable, AppTableToolbar } from "components/antd-ui";
import { ImportExcelModal, ImportExcelResult } from "components/Import/ImportExcelModal";
import {
  ActionButton,
  CodeCell,
  TableLayout,
  TableLayoutFooter,
  TableContent,
  getTableShowingRange,
} from "components/table";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { userStore } from "store/userStore";
import { ActivityLogLocationState } from "types/activityLog";
import { QueryParam } from "types/query";
import { Customer } from "types/customer";
import { PartnerOption } from "types/partner";
import { useRemoteOptions } from "hooks/useRemoteOptions";
import { Staff } from "types/staff";
import { getTitle } from "utils";
import { can } from "utils/auth";
import { formatDate } from "utils/date";
import { customerIndustryOptions, getCustomerSourceLabel, getCustomerTagColor } from "./customer.meta";
import { customerStageOptions, getCustomerStage, getRejectReasonLabel } from "config/customerStage";
import { formatMoney } from "views/Opportunity/opportunity.meta";
import { CustomerModal } from "./components/CustomerModal";
import { ContactsCell } from "./components/ContactsCell";
import "./Customer.scss";

const customerPath = "/customer";

type StaffOption = { label: string; value: number };

export const CustomerPage = observer(({ title = "" }) => {
  const [search, setSearch] = useState("");
  // Mở từ trang Phân tích khách hàng: nhận sẵn bộ lọc giai đoạn / sale / ngành / đối tác từ đường dẫn
  const [query, setQuery] = useState<QueryParam>(() => {
    const params = new URLSearchParams(window.location.search);
    const initial: QueryParam = { page: 1, limit: 50, search: "" };
    const stage = params.get("stage");
    const industry = params.get("industry");
    const staffId = Number(params.get("staffId")) || 0;
    const partnerId = Number(params.get("partnerId")) || 0;
    if (stage) initial.stage = stage;
    if (industry) initial.industry = industry;
    if (staffId) initial.staffId = staffId;
    if (partnerId) initial.partnerId = partnerId;
    return initial;
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Customer[]>([]);
  const [total, setTotal] = useState(0);
  const [staffOptions, setStaffOptions] = useState<StaffOption[]>([]);
  const [tagOptions, setTagOptions] = useState<{ label: string; value: string }[]>([]);
  const [importOpen, setImportOpen] = useState(false);
  const customerModalRef = useRef<CustomerModal>(null);
  const selectedStaffRef = useRef<StaffOption>();
  const staffOptionsRef = useRef<StaffOption[]>([]);
  const openedFromLogRef = useRef(false);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  staffOptionsRef.current = staffOptions;

  const onlyMine = !userStore.info.role?.isAdmin && can(customerPath, "ONLY_MINE");

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  useEffect(() => {
    if (openedFromLogRef.current) {
      return;
    }
    const customer = (location.state as ActivityLogLocationState | null)?.customer;
    if (!customer) {
      return;
    }
    openedFromLogRef.current = true;
    customerModalRef.current?.handleUpdate(customer);
  }, [location.state]);

  useEffect(() => {
    const customerId = Number(searchParams.get("customerId")) || 0;
    if (!customerId) {
      return;
    }

    let ignore = false;
    const load = async () => {
      const res = await customerApi.findOne(customerId);
      if (!ignore) {
        customerModalRef.current?.handleView(res.data as Customer);
      }
    };
    load();

    return () => {
      ignore = true;
    };
  }, [searchParams]);

  const closeCustomer = useCallback(() => {
    setSearchParams(
      (prev) => {
        if (!prev.get("customerId")) {
          return prev;
        }
        const next = new URLSearchParams(prev);
        next.delete("customerId");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const fetchData = useCallback(async (queryParams: QueryParam) => {
    setLoading(true);
    try {
      const res = await customerApi.findAll(queryParams);
      setData(res.data.customers);
      setTotal(res.data.total);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(query);
  }, [fetchData, query]);

  const handleDelete = useCallback(
    async (customer: Customer) => {
      setLoading(true);
      try {
        await customerApi.delete(customer.id);
        message.success("Xóa khách hàng thành công");
        await fetchData(query);
      } finally {
        setLoading(false);
      }
    },
    [fetchData, query],
  );

  const debounceSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        setQuery((prev) => ({ ...prev, search: keyword, page: 1 }));
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debounceSearch.cancel();
    };
  }, [debounceSearch]);

  const stopRowClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  const openView = useCallback((customer: Customer) => {
    customerModalRef.current?.handleView(customer);
  }, []);

  const openEdit = useCallback((customer: Customer) => {
    customerModalRef.current?.handleUpdate(customer);
  }, []);

  const openCreate = useCallback(() => {
    customerModalRef.current?.handleCreate();
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      debounceSearch(value);
    },
    [debounceSearch],
  );

  const handleClearSearch = useCallback(() => {
    setSearch("");
    debounceSearch.cancel();
    setQuery((prev) => ({ ...prev, page: 1, search: "" }));
  }, [debounceSearch]);

  const handlePageChange = useCallback(({ limit, page }: { limit: number; page: number }) => {
    setQuery((prev) => ({ ...prev, page, limit }));
  }, []);

  const fetchStaff = useCallback(async (keyword: string) => {
    const res = await staffApi.findAll({ page: 1, limit: 20, search: keyword });
    const staffs: Staff[] = res.data.staffs || [];
    const options = staffs.map((staff) => ({
      label: staff.name || staff.username,
      value: staff.id,
    }));
    const selected = selectedStaffRef.current;
    if (selected && !options.some((option) => option.value === selected.value)) {
      setStaffOptions([selected, ...options]);
      return;
    }
    setStaffOptions(options);
  }, []);

  const debounceSearchStaff = useMemo(
    () =>
      debounce((keyword: string) => {
        fetchStaff(keyword);
      }, 500),
    [fetchStaff],
  );

  useEffect(() => {
    if (onlyMine) {
      return;
    }
    fetchStaff("");
    return () => {
      debounceSearchStaff.cancel();
    };
  }, [debounceSearchStaff, fetchStaff, onlyMine]);

  const handleStaffChange = useCallback((value: string | number | undefined) => {
    const staffId = typeof value === "number" ? value : undefined;
    selectedStaffRef.current = staffId
      ? staffOptionsRef.current.find((option) => option.value === staffId)
      : undefined;
    setQuery((prev) => {
      const next: QueryParam = { ...prev, page: 1 };
      if (staffId) {
        next.staffId = staffId;
      } else {
        delete next.staffId;
      }
      return next;
    });
  }, []);

  const saleFilters = useMemo(
    () =>
      onlyMine
        ? []
        : [
          {
            key: "staffId",
            value: (query.staffId as number | undefined) ?? "",
            options: staffOptions,
            onChange: handleStaffChange,
            placeholder: "Sale chăm sóc",
            allowClear: true,
            showSearch: true,
            filterOption: false as const,
            onSearch: debounceSearchStaff,
            width: 220,
          },
        ],
    [debounceSearchStaff, handleStaffChange, onlyMine, query.staffId, staffOptions],
  );

  const loadTagOptions = useCallback(async () => {
    const res = await customerApi.findTags();
    setTagOptions(
      ((res.data.tags || []) as { name: string; total: number }[]).map((tag) => ({
        label: `${tag.name} (${tag.total})`,
        value: tag.name,
      })),
    );
  }, []);

  useEffect(() => {
    loadTagOptions();
  }, [loadTagOptions]);

  const refresh = useCallback(() => {
    fetchData(query);
    loadTagOptions();
  }, [fetchData, loadTagOptions, query]);

  const selectedTags = useMemo(
    () => (query.tags ? String(query.tags).split(",").filter(Boolean) : []),
    [query.tags],
  );

  const handleTagsChange = useCallback((values: string[]) => {
    setQuery((prev) => {
      const next: QueryParam = { ...prev, page: 1 };
      if (values.length) {
        next.tags = values.join(",");
      } else {
        delete next.tags;
      }
      return next;
    });
  }, []);

  const handleIndustryChange = useCallback((value: string | number | undefined) => {
    setQuery((prev) => {
      const next: QueryParam = { ...prev, page: 1 };
      if (value) {
        next.industry = String(value);
      } else {
        delete next.industry;
      }
      return next;
    });
  }, []);

  const industryFilter = useMemo(
    () => ({
      key: "industry",
      value: (query.industry as string | undefined) ?? "",
      options: customerIndustryOptions,
      onChange: handleIndustryChange,
      placeholder: "Ngành nghề",
      allowClear: true,
      width: 200,
    }),
    [handleIndustryChange, query.industry],
  );

  // Bộ lọc đối tác giới thiệu (tìm qua API)
  const partnerFilter = useRemoteOptions(
    useCallback(async (keyword: string) => {
      const res = await partnerApi.findOptions({ search: keyword });
      const partners: PartnerOption[] = res.data.partners || [];
      return partners.map((partner) => ({
        label: partner.company ? `${partner.name} - ${partner.company}` : partner.name,
        value: partner.id,
      }));
    }, []),
  );

  const partnerFilterConfig = useMemo(
    () => ({
      key: "partnerId",
      value: (query.partnerId as number | undefined) ?? "",
      options: partnerFilter.options,
      onChange: (value: string | number | undefined) => {
        const partnerId = typeof value === "number" ? value : undefined;
        partnerFilter.select(partnerId);
        setQuery((prev) => {
          const next: QueryParam = { ...prev, page: 1 };
          if (partnerId) {
            next.partnerId = partnerId;
          } else {
            delete next.partnerId;
          }
          return next;
        });
      },
      placeholder: "Đối tác giới thiệu",
      allowClear: true,
      showSearch: true,
      filterOption: false as const,
      onSearch: partnerFilter.onSearch,
      width: 220,
    }),
    [partnerFilter, query.partnerId],
  );

  const handleStageChange = useCallback((value: string | number | undefined) => {
    setQuery((prev) => {
      const next: QueryParam = { ...prev, page: 1 };
      if (value) {
        next.stage = String(value);
      } else {
        delete next.stage;
      }
      return next;
    });
  }, []);

  const stageFilter = useMemo(
    () => ({
      key: "stage",
      value: (query.stage as string | undefined) ?? "",
      options: customerStageOptions,
      onChange: handleStageChange,
      placeholder: "Phân loại",
      allowClear: true,
      width: 200,
    }),
    [handleStageChange, query.stage],
  );

  const canCreate = can(customerPath, "CREATE");
  const canEdit = can(customerPath, "UPDATE");
  const canDelete = can(customerPath, "DELETE");
  const isAdmin = !!userStore.info.role?.isAdmin;

  const openImport = useCallback(() => {
    setImportOpen(true);
  }, []);

  const closeImport = useCallback(() => {
    setImportOpen(false);
  }, []);

  const importCustomerFile = useCallback(async (file: File) => {
    const res = await customerApi.importExcel(file);
    return (res.data.results || []) as ImportExcelResult[];
  }, []);

  const columns = useMemo<TableColumnsType<Customer>>(
    () => [
      {
        title: "Mã",
        dataIndex: "code",
        key: "code",
        width: 120,
        render: (value) => <CodeCell value={value} />,
      },
      {
        title: "Tên",
        dataIndex: "name",
        key: "name",
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Phân loại",
        key: "stage",
        width: 170,
        render: (_, record) => {
          const stage = getCustomerStage(record.stage);
          if (!stage) return <TableContent>--</TableContent>;
          return (
            <div>
              <Tag color={stage.color}>{stage.label}</Tag>
              {record.stage === "REJECTED" && (
                <div className="text-xs text-gray-500">{getRejectReasonLabel(record.rejectCode)}</div>
              )}
            </div>
          );
        },
      },
      {
        title: "Nguồn",
        dataIndex: "source",
        key: "source",
        width: 120,
        render: (value) => <TableContent>{getCustomerSourceLabel(value) || "--"}</TableContent>,
      },
      {
        title: "Ngành nghề",
        dataIndex: "industry",
        key: "industry",
        width: 150,
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Người liên hệ",
        key: "contacts",
        width: 220,
        render: (_, record) => <ContactsCell customer={record} />,
      },
      {
        title: "Sale chăm sóc",
        key: "staff",
        width: 160,
        render: (_, record) => <TableContent>{record.staff?.name || "--"}</TableContent>,
      },
      {
        title: "Đối tác giới thiệu",
        key: "partner",
        width: 170,
        render: (_, record) => <TableContent>{record.partner?.name || "--"}</TableContent>,
      },
      {
        title: "Tags",
        key: "tags",
        width: 200,
        render: (_, record) =>
          record.tags?.length ? (
            <div className="flex flex-wrap gap-y-1">
              {record.tags.map((tag) => (
                <Tag key={tag} color={getCustomerTagColor(tag)} style={{ marginInlineEnd: 4 }}>
                  {tag}
                </Tag>
              ))}
            </div>
          ) : (
            <TableContent>--</TableContent>
          ),
      },
      {
        title: "Ghi chú gần nhất",
        key: "lastNote",
        width: 360,
        render: (_, record) => {
          const note = record.lastNote;
          if (!note) return <TableContent>--</TableContent>;
          const isSystem = note.type === "SYSTEM";
          return (
            <Tooltip title={<span style={{ whiteSpace: "pre-wrap" }}>{note.content}</span>}>
              <div className={`customerLastNote${isSystem ? " customerLastNote--system" : ""}`}>
                <div className="customerLastNote__content">{note.content}</div>
                <div className="customerLastNote__meta">
                  {isSystem && <span className="customerLastNote__badge">Hệ thống</span>}
                  {note.staffName && (
                    <span className="customerLastNote__metaItem">
                      <UserOutlined /> {note.staffName}
                    </span>
                  )}
                  {note.createdAt > 0 && (
                    <span className="customerLastNote__metaItem">
                      <ClockCircleOutlined /> {formatDate(note.createdAt)}
                    </span>
                  )}
                </div>
              </div>
            </Tooltip>
          );
        },
      },
      {
        title: "Tổng tiền đã mua",
        key: "totalAmount",
        align: "right" as const,
        width: 150,
        render: (_, record) => <TableContent>{formatMoney(record.totalAmount || 0)}</TableContent>,
      },
      {
        title: "Giá trị TB/đơn",
        key: "avgOrderAmount",
        align: "right" as const,
        width: 140,
        render: (_, record) => (
          <TableContent>{record.orderCount ? formatMoney(record.avgOrderAmount || 0) : "--"}</TableContent>
        ),
      },
      ...(canEdit || canDelete
        ? [
          {
            title: "Thao tác",
            key: "action",
            width: 100,
            align: "center" as const,
            render: (_: unknown, record: Customer) => (
              <div className="flex gap-2 justify-center items-center" onClick={stopRowClick}>
                {canEdit && (
                  <Tooltip title="Cập nhật">
                    <ActionButton aria-label="Cập nhật" onClick={() => openEdit(record)}>
                      <EditOutlined style={{ color: "#6c84e5" }} />
                    </ActionButton>
                  </Tooltip>
                )}
                {canDelete && (
                  <Tooltip title="Xóa">
                    <Popconfirm
                      cancelText="Không"
                      okText="Xóa"
                      onConfirm={() => handleDelete(record)}
                      title="Xác nhận xóa?"
                    >
                      <ActionButton aria-label="Xóa">
                        <DeleteOutlined style={{ color: "#ef4444" }} />
                      </ActionButton>
                    </Popconfirm>
                  </Tooltip>
                )}
              </div>
            ),
          },
        ]
        : []),
    ],
    [canDelete, canEdit, handleDelete, openEdit, stopRowClick],
  );

  const { showingFrom, showingTo } = getTableShowingRange(query.page, query.limit, total);

  return (
    <div className="customerPage">
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: search,
              width: 420,
              placeholder: "Tìm theo tên, mã, mã số thuế, liên hệ",
              onChange: handleSearchChange,
              inputProps: {
                allowClear: {
                  clearIcon: <CloseCircleFilled onClick={handleClearSearch} />,
                },
              },
            }}
            leftActions={
              <>
                {canCreate && (
                  <AppButton tone="primary" icon={<PlusOutlined />} onClick={openCreate}>
                    Tạo mới
                  </AppButton>
                )}
                {can(customerPath, "IMPORT") && (
                  <AppButton icon={<UploadOutlined />} onClick={openImport}>
                    Import Excel
                  </AppButton>
                )}
                <AppSelect
                  mode="multiple"
                  allowClear
                  maxTagCount="responsive"
                  style={{ minWidth: 220, maxWidth: 360 }}
                  placeholder="Lọc theo tag"
                  options={tagOptions}
                  value={selectedTags}
                  onChange={handleTagsChange}
                />
              </>
            }
            filterLabel={null}
            filters={[stageFilter, industryFilter, partnerFilterConfig, ...saleFilters]}
          />
        }
        table={
          <AppTable<Customer>
            rowKey="id"
            loading={loading}
            pagination={false}
            dataSource={data}
            columns={columns}
            onRow={(record) => ({
              onClick: () => openView(record),
            })}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={query.page}
            pageSize={query.limit}
            onChange={handlePageChange}
          />
        }
      />

      <CustomerModal ref={customerModalRef} onSubmitOk={refresh} onClose={closeCustomer} />
      <ImportExcelModal
        open={importOpen}
        title="Import khách hàng"
        sampleUrl="/templates/crm_customer.xlsx"
        sampleFileName="crm_customer.xlsx"
        onClose={closeImport}
        onSuccess={refresh}
        importFile={importCustomerFile}
      />
    </div>
  );
});
